import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Eye, EyeOff, TriangleAlert } from 'lucide-react';
import { MoneyText } from '@/ui';
import { toast } from '@/ui';
import { copyText } from '@/lib/clipboard';
import { formatIban, maskIban, normalizeIban } from '@/lib/iban-format';
import { OFFER_PRICE_FRACTION_DIGITS } from '@/lib/money';
import type { TotalAmount, UnitRate } from '@/lib/money';
import type { PhaseResult } from '@/lib/task-phase';
import type { BankAccount } from '@/lib/task-accounts';
import { TASK_STATUS_LABEL_KEYS } from '@/features/reports/labels';
import { CountdownRing } from './CountdownRing';
import { DirectionChip } from './DirectionChip';
import { PhaseBadge } from './PhaseBadge';
import styles from './QuoteCard.module.css';

/**
 * DORT LISTENIN ORTAK KARTI (musteri talebi, piyasa talebi, verilen teklif,
 * gelen teklif).
 *
 * Tek yerde durmasinin sebebi gorsel tutarliliktan fazlasi: faz, yon ve tutarin
 * NEREDE ve NASIL okundugu bu ekranlarin tamaminda ayni olmali — kullanici ayni
 * bilgiyi her listede ayni yerde aramali.
 */

const dateFormatter = new Intl.DateTimeFormat('tr-TR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** Kart ust satirindaki tarih. Disari acilmiyor (react-refresh kurali). */
const formatDateTime = (value: string | null): string => {
  if (!value) return '';
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? dateFormatter.format(parsed) : '';
};

/* -------------------------------------------------------------------------- */

export type QuoteCardProps = {
  phase: PhaseResult;
  /** Karara baglanmis ve KAZANAN satir — altin cerceve. */
  won?: boolean;
  /** Karara baglanmis ama kazanmamis satir — soluk. */
  lost?: boolean;
  children: ReactNode;
};

/** Kart kabugu. Sol kenardaki serit fazin rengini tasir. */
export function QuoteCard({ phase, won = false, lost = false, children }: QuoteCardProps) {
  const classes = [
    styles.card,
    styles[phase.phase],
    won ? styles.won : undefined,
    lost ? styles.lost : undefined,
  ]
    .filter(Boolean)
    .join(' ');
  return <article className={classes}>{children}</article>;
}

/* -------------------------------------------------------------------------- */

export type QuoteHeaderProps = {
  /** Gorunur kimlik (#12). Teklif satirlarinda GOREV kimligi gosterilir. */
  id: string;
  taskTypeCode: string;
  isBuying: boolean;
  /** Islem miktari — `taskAmount`; birimi ciftin base'i. */
  amount: TotalAmount;
  amountCurrency: string;
  createdAt: string | null;
  phase: PhaseResult;
  /** Karsi taraf "odeme yapilmadi" uyarisi almis. */
  warning?: boolean;
  /**
   * Sunucudaki `taskStatusCode`. KAPALI (passive) kartta etiket olarak yazilir: faz rozeti
   * orada hep "Istek Kapandi" diyor ve kapanisin NEDENINI (tamamlandi, suresi doldu, teklif
   * alinamadi, iade edildi...) soylemiyordu. Faz rozetine de gider: 'payment_received' ayni
   * 'payment' fazini tasiyor ama "Gonderim Bekliyor" okunmali (lib/task-phase.ts phaseLabelKey).
   */
  statusCode?: string | null | undefined;
  /** Faz rozetinin hazir metni (bkz. PhaseBadge `label`). */
  badgeLabel?: string | undefined;
};

export function QuoteHeader({
  id,
  taskTypeCode,
  isBuying,
  amount,
  amountCurrency,
  createdAt,
  phase,
  warning = false,
  statusCode,
  badgeLabel,
}: QuoteHeaderProps) {
  const { t } = useTranslation();
  const statusKey =
    phase.phase === 'passive' && statusCode ? TASK_STATUS_LABEL_KEYS[statusCode] : undefined;

  return (
    <header className={styles.head}>
      <span className={styles.pair}>{taskTypeCode}</span>

      <div className={styles.middle}>
        <div className={styles.topRow}>
          <PhaseBadge phase={phase.phase} taskStatusCode={statusCode} label={badgeLabel} />
          <DirectionChip isBuying={isBuying} />
          {warning ? (
            <span className={styles.warning} title={t('task.warning')}>
              <TriangleAlert size={14} strokeWidth={2.2} aria-hidden="true" />
              <span className="visually-hidden">{t('task.warning')}</span>
            </span>
          ) : null}
        </div>

        {/* Kahraman tutar — mobil QuoteHeader ile ayni vurgu kademesi. */}
        <MoneyText total={amount} currency={amountCurrency} size="display" />

        <div className={styles.metaRow}>
          <span>#{id}</span>
          <span>{formatDateTime(createdAt)}</span>
          {statusKey === undefined ? null : <span>{t(statusKey)}</span>}
        </div>
      </div>

      <CountdownRing phase={phase} taskStatusCode={statusCode} />
    </header>
  );
}

/* -------------------------------------------------------------------------- */

export type InfoItem = {
  label: string;
  /** Birim kur (fiyat). `total` ile ayni anda VERILEMEZ. */
  rate?: UnitRate;
  /** Islem toplami. */
  total?: TotalAmount;
  /** Duz metin (karsi taraf adi gibi). */
  text?: string;
  currency?: string;
  emphasis?: boolean;
};

/**
 * Etiket-deger izgarasi.
 *
 * Parasal degerler `MoneyText` uzerinden ve HANGI TURDE olduklarini soyleyerek
 * gecer: `rate` birim kur, `total` islem toplami. Ikisi ayni prop'tan gecseydi
 * biri digerinin yerine yazilabilirdi — gecmiste yazildi da.
 */
export function InfoGrid({ items }: { items: readonly InfoItem[] }) {
  return (
    <dl className={styles.grid}>
      {items.map((item) => (
        <div key={item.label} className={styles.cell}>
          <dt className={styles.cellLabel}>{item.label}</dt>
          <dd className={styles.cellValue}>
            {item.rate !== undefined ? (
              <MoneyText
                rate={item.rate}
                // Bu izgaradaki her `rate` bir TEKLIF FIYATIDIR (offer_price), TCMB piyasa
                // kuru degil. Kolon numeric(11,2) oldugundan varsayilan 4 hane, sunucunun
                // saklayamadigi bir hassasiyeti gosteriyordu.
                fractionDigits={OFFER_PRICE_FRACTION_DIGITS}
                {...(item.currency === undefined ? {} : { currency: item.currency })}
                size={item.emphasis ? 'h3' : 'body'}
                tone={item.emphasis ? 'primary' : 'text'}
              />
            ) : item.total !== undefined ? (
              <MoneyText
                total={item.total}
                {...(item.currency === undefined ? {} : { currency: item.currency })}
                size={item.emphasis ? 'h3' : 'body'}
                tone={item.emphasis ? 'primary' : 'text'}
              />
            ) : (
              <span className={styles.cellText}>{item.text}</span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/* -------------------------------------------------------------------------- */

export type BankPanelProps = {
  account: BankAccount | undefined;
};

/**
 * Takas hesabi: hangi bankada, hangi hesap, hangi IBAN.
 *
 * KATLANMIYOR (2026-08-30). Eskiden "Banka bilgileri" dugmesinin arkasindaydi;
 * listede gosterilen icerik iki-uc satirdan ibaret oldugu icin acilir kapanir
 * olmasi kullaniciya her kartta fazladan bir tiklama yuklüyordu. Artik kartin
 * bir seridi olarak dogrudan duruyor.
 *
 * IBAN YINE DE MASKELI. Bu, panelin gizlenmesiyle ayni sey degil: maskeleme
 * lib/iban-format.ts'te yazili bir URUN KARARI ("liste satirlari IBAN'in
 * TAMAMINI GOSTERMEZ") ve hesap ekrani da (AccountsPage) ayni deseni izliyor.
 * Panel surekli acik hale gelince tam IBAN'i basmak, onceye gore DAHA fazla
 * ifsa olurdu — o yuzden maske + "goster" + "kopyala" ucluleri burada da ayni.
 * Kopyalama tam degeri verir; elle okunup yazilan bir IBAN'da hata yapmak bu
 * ekranda yapilabilecek EN PAHALI hatadir.
 */
export function BankPanel({ account }: BankPanelProps) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState(false);

  const copyIban = (): void => {
    const iban = account?.accountIbanCode;
    if (!iban) return;
    // Pano API'si guvenli baglam (https / localhost) ister; YOKSA ya da reddedilirse
    // sessiz kalmiyoruz, kullanici IBAN'i kopyaladigini SANMAMALI (lib/clipboard.ts).
    // Eskiden korumasiz `navigator.clipboard.writeText` guvensiz baglamda TypeError
    // firlatiyordu ve hata kolu hic calismiyordu.
    void copyText(normalizeIban(iban)).then((copied) => {
      toast(
        copied
          ? { message: t('task.ibanCopied'), tone: 'success' }
          : { message: t('accounts.copyFailed'), tone: 'danger' },
      );
    });
  };

  if (!account) return null;

  const iban = account.accountIbanCode;

  return (
    <section className={styles.bank} aria-label={t('labels.bankDetails')}>
      <p className={styles.cellLabel}>{t('labels.bankDetails')}</p>

      <div className={styles.bankLine}>
        <span className={styles.bankName}>{account.accountLabel ?? account.accountCode}</span>
        {account.accountName ? (
          <span className={styles.bankMeta}>{account.accountName}</span>
        ) : null}
        <span className={styles.bankCurrency}>{account.accountCurrency}</span>
      </div>

      {iban ? (
        <div className={styles.ibanRow}>
          <span className={styles.iban}>{revealed ? formatIban(iban) : maskIban(iban)}</span>
          <span className={styles.ibanActions}>
            <button
              type="button"
              className={styles.iconButton}
              aria-label={revealed ? t('accounts.hideIban') : t('accounts.showIban')}
              aria-pressed={revealed}
              onClick={() => {
                setRevealed((value) => !value);
              }}
            >
              {revealed ? (
                <EyeOff size={14} aria-hidden="true" />
              ) : (
                <Eye size={14} aria-hidden="true" />
              )}
            </button>
            <button
              type="button"
              className={styles.iconButton}
              onClick={copyIban}
              aria-label={t('accounts.copyIban')}
            >
              <Copy size={14} aria-hidden="true" />
            </button>
          </span>
        </div>
      ) : null}
    </section>
  );
}

/** Kartin alt eylem satiri. Ust cizgi eylemleri bilgiden ayirir. */
export function QuoteActions({ children }: { children: ReactNode }) {
  return <div className={styles.actions}>{children}</div>;
}

/**
 * Eylem satirinin YERINE kisa bilgi notu: dugme neden yok (orn. is arkadasinin teklifi var ama
 * `editOffer` yetkisi yok, 2026-10-03). Ayni yerde durur, ayni ust cizgiyle ayrilir.
 */
export function QuoteNote({ children }: { children: ReactNode }) {
  return <p className={styles.note}>{children}</p>;
}
